import { Avatar, Box, Stack, Typography, CardMedia } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VizualizadorImagem from '../../components/VizualizadorImagem';
import AudioPlayerWithSpeed from '../../components/AudioPlayerWithSpeed';
import PdfPreview from '../../components/PdfPreview';

function formatMessageTime(value) {
  if (!value) return '';
  const raw = String(value).trim();
  const hhmmss = raw.match(/^(\d{2}):(\d{2})(:\d{2})?$/);
  if (hhmmss) return `${hhmmss[1]}:${hhmmss[2]}`;
  const dmyHms = raw.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2})(:\d{2})?$/);
  if (dmyHms) return `${dmyHms[4]}:${dmyHms[5]}`;
  const iso = new Date(raw);
  if (!Number.isNaN(iso.getTime())) {
    const hh = String(iso.getHours()).padStart(2, '0');
    const mm = String(iso.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }
  return raw.slice(0, 5);
}

function getReplyPreview(replyTo) {
  if (!replyTo) return '';
  if (replyTo.msgType === 'file') {
    const rawType =
      (replyTo.msgFileType || `${replyTo.urlFile || ''}`.split('.').pop() || '').toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(rawType)) return 'Foto';
    if (['m4a', 'mp3', 'wav', 'aac', 'ogg', 'webm'].includes(rawType)) return 'Audio';
    if (['mp4', 'mov', '3gpp', 'mkv'].includes(rawType)) return 'Video';
    if (rawType === 'pdf') return 'Documento PDF';
    return 'Arquivo';
  }
  return replyTo.msgText || 'Mensagem';
}

function renderReplyPreview(replyTo, baseUrlMedia) {
  if (!replyTo) return null;
  if (replyTo.msgType !== 'file') {
    return <Typography variant="body2" sx={{ color: 'text.primary' }}>{replyTo.msgText || 'Mensagem'}</Typography>;
  }

  const fileType =
    (replyTo.msgFileType || `${replyTo.urlFile || ''}`.split('.').pop() || '').toLowerCase();
  let url = replyTo.urlFile || '';
  if (url && !url.startsWith('http') && baseUrlMedia) {
    url = `${baseUrlMedia}/${url}`;
  }
  const label = getReplyPreview(replyTo);
  const commonThumbSx = {
    width: 44,
    height: 44,
    borderRadius: 1,
    objectFit: 'cover',
    border: '1px solid rgba(0,0,0,0.12)',
    bgcolor: 'background.paper',
  };

  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(fileType) && url) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="img" src={url} alt="midia" sx={commonThumbSx} />
        <Typography variant="body2" sx={{ color: 'text.primary' }}>{label}</Typography>
      </Box>
    );
  }
  if (['mp4', 'mov', '3gpp', 'mkv'].includes(fileType) && url) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="video" src={url} muted sx={commonThumbSx} />
        <Typography variant="body2" sx={{ color: 'text.primary' }}>{label}</Typography>
      </Box>
    );
  }
  if (['m4a', 'mp3', 'wav', 'aac', 'ogg', 'webm'].includes(fileType)) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GraphicEqIcon fontSize="small" />
        <Typography variant="body2" sx={{ color: 'text.primary' }}>{label}</Typography>
      </Box>
    );
  }
  if (fileType === 'pdf') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PictureAsPdfIcon fontSize="small" />
        <Typography variant="body2" sx={{ color: 'text.primary' }}>{label}</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <InsertDriveFileIcon fontSize="small" />
      <Typography variant="body2" sx={{ color: 'text.primary' }}>{label}</Typography>
    </Box>
  );
}

function isAutomaticMessage(text = '') {
  const upper = String(text).toUpperCase();
  return upper.includes('MENSAGEM AUTOMATICA');
}

function MensagemSuporte({ mensage, onReply }) {
  const theme = useTheme();
  const baseUrlMedia = process.env.REACT_APP_URL_ARQUIVOS;
  const urlBaseImage = process.env.REACT_APP_BASE_NAME;

  const hideReplyButton = isAutomaticMessage(mensage?.mensage || '');
  const incomingType = `${mensage?.tipoArquivo || ''}`.toLowerCase();
  const isAudioMessage = ['m4a', 'mp3', 'wav', 'aac', 'ogg', 'webm'].includes(incomingType);

  let mediaUrl = mensage?.previewPath;
  if (mensage?.filePath) {
    mediaUrl = mensage.filePath.startsWith('http')
      ? mensage.filePath
      : `${baseUrlMedia}/${mensage.filePath}`;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row-reverse', marginBottom: 1 }}>
        <Avatar
          alt="Nome Avatar"
          src={`${urlBaseImage}/static/avatars/avatar_default.jpg`}
          sx={{
            marginLeft: 2,
            width: 35,
            height: 35,
            background: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }}
        />
        <Stack
          sx={{
            width: 'fit-content',
            maxWidth: { xs: 'calc(100% - 52px)', sm: 'calc(100% - 64px)' },
            alignItems: 'flex-end',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              marginLeft: { xs: 0, sm: 10 },
              width: 'fit-content',
              backgroundColor: isAudioMessage ? 'transparent' : theme.palette.background.default,
              padding: isAudioMessage ? 0 : 1,
              borderRadius: isAudioMessage ? 0 : 1,
            }}
          >
            {mensage?.replyTo && (
              <Box
                sx={{
                  mb: 1,
                  p: 0.8,
                  borderRadius: 1,
                  borderLeft: '3px solid rgba(0,0,0,0.35)',
                  bgcolor: 'rgba(0,0,0,0.06)',
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Respondendo
                </Typography>
                {renderReplyPreview(mensage.replyTo, baseUrlMedia)}
              </Box>
            )}

            {mensage?.mensage ? mensage?.mensage : ''}

            {mensage?.tipoArquivo === 'png' ||
            mensage?.tipoArquivo === 'jpg' ||
            mensage?.tipoArquivo === 'jpeg' ? (
              <VizualizadorImagem arquivo={mediaUrl} />
            ) : (
              ''
            )}

            {mensage?.tipoArquivo === 'pdf' ? (
              <PdfPreview urlFile={mediaUrl} />
            ) : (
              ''
            )}

            {mensage?.tipoArquivo === 'mp4' ||
            mensage?.tipoArquivo === '3gpp' ||
            mensage?.tipoArquivo === 'mov' ? (
              <CardMedia component="video" height={250} image={mediaUrl} controls />
            ) : (
              ''
            )}

            {mensage?.tipoArquivo === 'm4a' ||
            mensage?.tipoArquivo === 'mp3' ||
            mensage?.tipoArquivo === 'wav' ||
            mensage?.tipoArquivo === 'aac' ||
            mensage?.tipoArquivo === 'ogg' ||
            mensage?.tipoArquivo === 'webm' ? (
              <Box
                sx={{
                  minWidth: 0,
                  width: { xs: 'min(320px, calc(100vw - 122px))', sm: '100%' },
                  maxWidth: { xs: 'calc(100vw - 122px)', sm: 520 },
                  overflow: 'hidden',
                  p: 1.2,
                  borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.08)',
                  backgroundColor: theme.palette.background.default,
                  boxShadow: 'none',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <GraphicEqIcon fontSize="small" />
                  <Typography variant="caption" fontWeight={700}>
                    Mensagem de voz
                  </Typography>
                </Box>
                <AudioPlayerWithSpeed src={mediaUrl} tone="outgoing" />
                {!hideReplyButton && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', cursor: 'pointer', mt: 0.4, display: 'block' }}
                    onClick={() => onReply?.(mensage)}
                  >
                    Responder
                  </Typography>
                )}
              </Box>
            ) : (
              ''
            )}

            {!hideReplyButton && !isAudioMessage && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', cursor: 'pointer', mt: 0.6, display: 'block' }}
                onClick={() => onReply?.(mensage)}
              >
                Responder
              </Typography>
            )}
          </Box>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.disabled, textAlign: 'right', alignSelf: 'flex-end', mt: 0.35 }}
          >
            {formatMessageTime(mensage?.data)}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

export default MensagemSuporte;

